import { Box, Collapse, Divider, Stack, Tooltip, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1DisplayCondition } from 'components/core/condition-builder/C1DisplayCondition'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Step } from 'components/pages/policy/detail/PolicyStep'
import { policyTypeToString } from 'components/pages/policy/type'
import { ChevronDown, ChevronUp, Info } from 'lucide-react'
import { Policy, PolicyStep as PolicyStepProto, PolicyType, Rule } from 'pbts/c1/api/policy/v1/policy'
import { FC, useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

type PolicyDraftPreviewProps = {
    policy: Policy
    /** If true, starts with rules expanded */
    defaultExpanded?: boolean
}

type PolicyRulePreviewProps = {
    rule?: Rule
    steps: PolicyStepProto[]
    policyType: PolicyType
    index?: number
    isBaseline?: boolean
}

const chipStyles = {
    width: '60px',
    height: '30px',
    textTransform: 'uppercase',
}

/**
 * Read-only preview of a single policy rule with its steps.
 * Simplified version of PolicyRule without edit functionality.
 */
const PolicyRulePreview: FC<PolicyRulePreviewProps> = ({
    rule,
    steps,
    policyType,
    index = 0,
    isBaseline = false,
}) => (
    <Box
        sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: C1BorderRadius.regular,
            backgroundColor: 'background.default',
        }}
    >
        {/* Rule header */}
        <Stack direction="row" alignItems="center" spacing={1}>
            {isBaseline ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" component="span">
                        <FormattedMessage
                            defaultMessage="Baseline rule"
                            id="copilot_policy_preview_baseline_rule"
                        />
                    </Typography>
                    <Tooltip
                        title={
                            <Typography>
                                <FormattedMessage
                                    defaultMessage="The baseline rule is applied if no other rules match."
                                    id="copilot_policy_preview_baseline_tooltip"
                                />
                            </Typography>
                        }
                        placement="top"
                    >
                        <C1Icon icon={Info} size="sm" sx={{ color: 'action.active' }} />
                    </Tooltip>
                </Stack>
            ) : (
                <C1Chip
                    sx={{
                        ...chipStyles,
                        backgroundColor: 'primary.main',
                        borderColor: 'primary.dark',
                        color: 'primary.contrastText',
                    }}
                    color="primary"
                    label={<FormattedNumber value={index + 1} />}
                />
            )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Condition - only for non-baseline rules */}
        {!isBaseline && rule?.condition && (
            <>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <C1Chip
                        sx={chipStyles}
                        color="primary"
                        label={
                            <FormattedMessage
                                id="copilot_policy_preview_if"
                                defaultMessage="if"
                            />
                        }
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <C1DisplayCondition expression={rule.condition} />
                    </Box>
                </Stack>
                <Divider sx={{ my: 2 }} />
            </>
        )}

        {/* Steps */}
        <Stack direction="row" spacing={2} alignItems="flex-start">
            {!isBaseline && (
                <C1Chip
                    sx={chipStyles}
                    color="primary"
                    label={
                        <FormattedMessage
                            id="copilot_policy_preview_then"
                            defaultMessage="then"
                        />
                    }
                />
            )}
            <Box sx={{ flexGrow: 1, pt: isBaseline ? 0 : 1 }}>
                {steps.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage
                            defaultMessage="No steps configured"
                            id="copilot_policy_preview_no_steps"
                        />
                    </Typography>
                ) : (
                    steps.map((step, i) => (
                        <Step
                            key={i}
                            step={step}
                            policyType={policyType}
                            index={i}
                        />
                    ))
                )}
            </Box>
        </Stack>
    </Box>
)

/**
 * Counts the total number of steps across all flows in a policy.
 */
const countPolicySteps = (policy: Policy): number => {
    let count = 0
    for (const steps of Object.values(policy.policySteps)) {
        count += steps?.steps?.length ?? 0
    }
    return count
}

/**
 * Read-only preview of a policy draft from the AI assistant.
 * Uses the same Step component as the detail page for consistent rendering.
 *
 * Shows all rules and steps in a collapsible format with a summary header.
 */
export const PolicyDraftPreview: FC<PolicyDraftPreviewProps> = ({
    policy,
    defaultExpanded = false,
}) => {
    const intl = useIntl()
    const [isExpanded, setIsExpanded] = useState(defaultExpanded)

    const defaultPolicyKey = policyTypeToString(policy.policyType)
    const baselineSteps = policy.policySteps[defaultPolicyKey]?.steps ?? []
    const conditionalRules = policy.rules ?? []

    const stepCount = countPolicySteps(policy)
    const ruleCount = conditionalRules.length

    // Check if there's anything to show
    const hasContent = baselineSteps.length > 0 || conditionalRules.length > 0

    if (!hasContent) {
        return (
            <Box
                sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: C1BorderRadius.regular,
                    backgroundColor: 'grey.50',
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                        defaultMessage="No rules or steps configured yet."
                        id="copilot_policy_preview_empty"
                    />
                </Typography>
            </Box>
        )
    }

    return (
        <Box>
            {/* Summary header with expand/collapse */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: isExpanded ? 'primary.main' : 'divider',
                    borderRadius: isExpanded
                        ? `${C1BorderRadius.regular} ${C1BorderRadius.regular} 0 0`
                        : C1BorderRadius.regular,
                    backgroundColor: isExpanded ? 'primary.extraLight' : 'grey.50',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'primary.extraLight',
                    },
                }}
                onClick={(): void => setIsExpanded(!isExpanded)}
            >
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body1">
                        <FormattedMessage
                            defaultMessage="Rules"
                            id="copilot_policy_preview_header"
                        />
                    </Typography>
                    <C1Chip
                        size="small"
                        label={intl.formatMessage({
                            id: 'copilot_policy_preview_steps_count',
                            defaultMessage: '{count, plural, one {# step} other {# steps}}',
                        }, { count: stepCount })}
                    />
                    {ruleCount > 0 && (
                        <C1Chip
                            size="small"
                            color="info"
                            label={intl.formatMessage({
                                id: 'copilot_policy_preview_rules_count',
                                defaultMessage: '{count, plural, one {# conditional rule} other {# conditional rules}}',
                            }, { count: ruleCount })}
                        />
                    )}
                </Stack>
                <C1Button
                    variant="text"
                    endIcon={isExpanded ? <C1Icon icon={ChevronUp} /> : <C1Icon icon={ChevronDown} />}
                    onClick={(e): void => {
                        e.stopPropagation()
                        setIsExpanded(!isExpanded)
                    }}
                >
                    {isExpanded ? (
                        <FormattedMessage
                            defaultMessage="Hide details"
                            id="copilot_policy_preview_hide"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Show details"
                            id="copilot_policy_preview_show"
                        />
                    )}
                </C1Button>
            </Stack>

            {/* Expandable content */}
            <Collapse in={isExpanded}>
                <Stack
                    spacing={2}
                    sx={{
                        p: 2,
                        border: '1px solid',
                        borderTop: 0,
                        borderColor: 'primary.main',
                        borderRadius: `0 0 ${C1BorderRadius.regular} ${C1BorderRadius.regular}`,
                        backgroundColor: 'background.paper',
                    }}
                >
                    {/* Conditional rules first */}
                    {conditionalRules.map((rule, idx) => (
                        <PolicyRulePreview
                            key={rule.policyKey || idx}
                            rule={rule}
                            steps={policy.policySteps[rule.policyKey]?.steps ?? []}
                            policyType={policy.policyType}
                            index={idx}
                        />
                    ))}

                    {/* Baseline rule last */}
                    {baselineSteps.length > 0 && (
                        <PolicyRulePreview
                            isBaseline
                            steps={baselineSteps}
                            policyType={policy.policyType}
                        />
                    )}
                </Stack>
            </Collapse>
        </Box>
    )
}
